import { inr, num, fmtDate, DOC_LABEL } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";

export const InvoiceDocument = ({ doc, org }) => {
  if (!doc) return null;
  const t = doc.totals || {};
  const isCN = doc.document_type === "CREDIT_NOTE";
  // A composition or unregistered supplier may not collect GST, so the tax
  // columns are omitted entirely rather than printed as zeroes. Fall back to
  // the org for documents written before supplier_gst_status existed.
  const gstStatus =
    doc.supplier_gst_status ||
    (org?.registration_type === "REGULAR" && org?.gstin ? "REGULAR" : org?.registration_type) ||
    "REGULAR";
  const showGst = gstStatus === "REGULAR";
  const isIntra = showGst && doc.supply_type === "INTRA_STATE";
  const sgstLabel = doc.uses_utgst ? "UTGST" : "SGST";
  const headingLabel = isCN
    ? "Credit Note"
    : showGst
      ? DOC_LABEL[doc.document_type] || "Invoice"
      : gstStatus === "COMPOSITION"
        ? "Bill of Supply"
        : "Invoice";

  return (
    <div className="bg-white border border-slate-200 rounded-md print:border-0" data-testid="invoice-document">
      <div className="flex flex-col sm:flex-row justify-between gap-6 p-6 sm:p-8 border-b border-slate-200">
        <div>
          <div className="font-heading text-xl font-bold text-slate-900" data-testid="doc-org-name">
            {org?.legal_name || org?.name}
          </div>
          <div className="mt-1.5 text-xs leading-relaxed text-slate-600 max-w-xs">
            {org?.address_line1}
            {org?.city ? `, ${org.city}` : ""} {org?.pincode}
            {org?.state_name ? <div>{org.state_name}</div> : null}
            {org?.phone ? <div>{org.phone}</div> : null}
            {org?.email ? <div>{org.email}</div> : null}
          </div>
          {/* Never print a GSTIN on a document that charges no GST — the org may
              still hold a stale GSTIN after switching to unregistered. */}
          {showGst && org?.gstin && (
            <div className="mt-2 font-mono text-xs font-semibold text-slate-900">GSTIN: {org.gstin}</div>
          )}
          {gstStatus === "COMPOSITION" && (
            <div className="mt-2 text-[11px] italic text-slate-600 max-w-xs">
              Composition taxable person, not eligible to collect tax on supplies.
            </div>
          )}
          {gstStatus !== "COMPOSITION" && !showGst && (
            <div className="mt-2 text-[11px] italic text-slate-600 max-w-xs">
              Not registered under GST. No tax is charged on this invoice.
            </div>
          )}
        </div>
        <div className="sm:text-right">
          <div className="text-[11px] uppercase tracking-[0.18em] text-blue-600 font-semibold">
            {headingLabel}
          </div>
          <div className="font-mono text-2xl font-bold text-slate-900 mt-1" data-testid="doc-number">
            {doc.document_number || "DRAFT"}
          </div>
          <div className="mt-2 text-xs text-slate-600 space-y-0.5">
            <div>Date: <span className="font-mono">{fmtDate(doc.document_date)}</span></div>
            {!isCN && <div>Due: <span className="font-mono">{fmtDate(doc.due_date)}</span></div>}
            {doc.reference_document?.document_number && (
              <div data-testid="doc-reference">
                Against: <span className="font-mono">{doc.reference_document.document_number}</span> dated{" "}
                {fmtDate(doc.reference_document.document_date)}
              </div>
            )}
          </div>
          <div className="mt-2 flex sm:justify-end gap-1.5">
            <StatusBadge status={doc.supply_type} testid="doc-supply-type" />
            {doc.is_reverse_charge && <StatusBadge status="OVERDUE" label="RCM" />}
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-200 border-b border-slate-200">
        <div className="p-6 sm:p-8">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Bill To</div>
          <div className="mt-2 font-semibold text-slate-900" data-testid="doc-party-name">{doc.party_name}</div>
          <div className="text-xs text-slate-600 mt-1 leading-relaxed">{doc.billing_address || "—"}</div>
          {doc.party_gstin && (
            <div className="font-mono text-xs mt-1.5 font-semibold">GSTIN: {doc.party_gstin}</div>
          )}
          {doc.party_phone && <div className="font-mono text-xs text-slate-600">{doc.party_phone}</div>}
        </div>
        <div className="p-6 sm:p-8">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Ship To</div>
          <div className="text-xs text-slate-600 mt-2 leading-relaxed">
            {doc.shipping_address || doc.billing_address || "—"}
          </div>
          <div className="mt-3 text-xs text-slate-600">
            Place of Supply:{" "}
            <span className="font-semibold text-slate-900" data-testid="doc-place-of-supply">
              {doc.place_of_supply_name} ({doc.place_of_supply_code})
            </span>
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            Reverse charge: <span className="font-semibold">{doc.is_reverse_charge ? "Yes" : "No"}</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="doc-lines-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-600">
            <tr>
              <th className="px-3 py-2.5 text-left font-semibold">#</th>
              <th className="px-3 py-2.5 text-left font-semibold">Description</th>
              {showGst && <th className="px-3 py-2.5 text-left font-semibold">HSN/SAC</th>}
              <th className="px-3 py-2.5 text-right font-semibold">Qty</th>
              <th className="px-3 py-2.5 text-right font-semibold">Rate</th>
              <th className="px-3 py-2.5 text-right font-semibold">Disc</th>
              <th className="px-3 py-2.5 text-right font-semibold">Taxable</th>
              {showGst &&
                (isIntra ? (
                  <>
                    <th className="px-3 py-2.5 text-right font-semibold">CGST</th>
                    <th className="px-3 py-2.5 text-right font-semibold">{sgstLabel}</th>
                  </>
                ) : (
                  <th className="px-3 py-2.5 text-right font-semibold">IGST</th>
                ))}
              <th className="px-3 py-2.5 text-right font-semibold">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(doc.lines || []).map((l) => (
              <tr key={l.line_number} className="tabular-nums">
                <td className="px-3 py-2.5 text-slate-500">{l.line_number}</td>
                <td className="px-3 py-2.5">
                  <div className="font-medium text-slate-900">{l.item_name}</div>
                  {l.description && <div className="text-[11px] text-slate-500">{l.description}</div>}
                </td>
                {showGst && (
                  <td className="px-3 py-2.5 font-mono text-slate-600">{l.hsn_sac_code}</td>
                )}
                <td className="px-3 py-2.5 text-right font-mono">
                  {num(l.quantity, 3)} <span className="text-slate-400">{l.unit_symbol}</span>
                </td>
                <td className="px-3 py-2.5 text-right font-mono">{num(l.rate)}</td>
                <td className="px-3 py-2.5 text-right font-mono">
                  {Number(l.discount_amount) ? num(l.discount_amount) : "—"}
                </td>
                <td className="px-3 py-2.5 text-right font-mono">{num(l.taxable_value)}</td>
                {showGst &&
                  (isIntra ? (
                    <>
                      <td className="px-3 py-2.5 text-right font-mono">
                        {num(l.cgst_amount)}
                        <div className="text-[10px] text-slate-400">{num(l.cgst_rate, 2)}%</div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">
                        {num(l.sgst_amount)}
                        <div className="text-[10px] text-slate-400">{num(l.sgst_rate, 2)}%</div>
                      </td>
                    </>
                  ) : (
                    <td className="px-3 py-2.5 text-right font-mono">
                      {num(l.igst_amount)}
                      <div className="text-[10px] text-slate-400">{num(l.igst_rate, 2)}%</div>
                    </td>
                  ))}
                <td className="px-3 py-2.5 text-right font-mono font-semibold">{num(l.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid sm:grid-cols-2 gap-6 p-6 sm:p-8 border-t border-slate-200">
        <div className="space-y-4 text-xs">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Amount in words</div>
            <div className="mt-1 font-medium text-slate-900" data-testid="doc-amount-words">
              {doc.amount_in_words}
            </div>
          </div>
          {(org?.bank_name || org?.upi_id) && (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Bank details</div>
              <div className="mt-1 text-slate-700 font-mono leading-relaxed">
                {org.bank_name && <div>{org.bank_name}</div>}
                {org.bank_account_number && <div>A/C: {org.bank_account_number}</div>}
                {org.bank_ifsc && <div>IFSC: {org.bank_ifsc}</div>}
                {org.upi_id && <div>UPI: {org.upi_id}</div>}
              </div>
            </div>
          )}
          {doc.terms_and_conditions && (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Terms</div>
              <div className="mt-1 whitespace-pre-line text-slate-600">{doc.terms_and_conditions}</div>
            </div>
          )}
          {doc.notes && <div className="text-slate-600 italic">{doc.notes}</div>}
        </div>

        <div className="text-sm">
          <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
            <Row label="Sub total (taxable)" value={t.taxable_value} />
            {Number(t.document_discount_amount) ? (
              <Row label="Document discount" value={-t.document_discount_amount} />
            ) : null}
            {showGst &&
              (isIntra ? (
                <>
                  <Row label="CGST" value={t.cgst_amount} />
                  <Row label={sgstLabel} value={t.sgst_amount} />
                </>
              ) : (
                <Row label="IGST" value={t.igst_amount} />
              ))}
            {showGst && Number(t.cess_amount) ? <Row label="Cess" value={t.cess_amount} /> : null}
            {Number(t.tcs_amount) ? (
              <Row label={`TCS @${Number(t.tcs_rate)}% u/s ${t.tcs_section || ""}`}
                value={t.tcs_amount} />
            ) : null}
            <Row label="Round off" value={t.round_off} />
            <div className={`flex items-center justify-between px-3 py-3 bg-slate-900 text-white ${
              Number(t.tds_amount) ? "" : "rounded-b-md"}`}>
              <span className="text-xs uppercase tracking-widest font-semibold">Grand total</span>
              <span className="font-mono text-lg font-bold" data-testid="doc-grand-total">
                {inr(t.grand_total)}
              </span>
            </div>
            {/* Below the grand total, because the invoice is for the full
                amount and this is only what the customer will remit. */}
            {Number(t.tds_amount) ? (
              <>
                <Row label={`Less: TDS @${Number(t.tds_rate)}% u/s ${t.tds_section || ""}`}
                  value={-Number(t.tds_amount)} />
                <div className="flex items-center justify-between px-3 py-2.5 border-t border-slate-200 rounded-b-md bg-slate-50">
                  <span className="text-xs font-semibold text-slate-700">Amount payable</span>
                  <span className="font-mono text-base font-bold" data-testid="doc-net-receivable">
                    {inr(t.net_receivable)}
                  </span>
                </div>
              </>
            ) : null}
          </div>
          {!isCN && (
            <div className="mt-3 space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">Amount paid</span>
                <span className="font-mono" data-testid="doc-amount-paid">{inr(doc.amount_paid)}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span className="text-slate-700">Balance due</span>
                <span data-testid="doc-balance-due"
                  className={`font-mono ${Number(doc.balance_due || 0) < 0.01
                    ? "text-emerald-700" : "text-rose-700"}`}>
                  {inr(doc.balance_due)}
                </span>
              </div>
            </div>
          )}
          <div className="mt-8 text-right text-xs text-slate-500">
            <div>For <span className="font-semibold text-slate-800">{org?.legal_name || org?.name}</span></div>
            <div className="mt-10">
              {/* The PDF honours org.signature_label; this view was hardcoded,
                  so a merchant who set their own label saw it on the printed
                  invoice and not on the one they shared. */}
              <span className="inline-block border-t border-slate-300 pt-1.5">
                {org?.signature_label || "Authorised Signatory"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between px-3 py-2">
    <span className="text-slate-600 text-xs">{label}</span>
    <span className="font-mono text-sm tabular-nums">{inr(value)}</span>
  </div>
);

export default InvoiceDocument;
